<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Lifecycle
	import { onMount } from 'svelte';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Design system components
	import { Button, Input, Select, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { valueOutOfRange } from 'src/helpers/utilities';

	let className = '';
	export { className as class };

	export let device: any;
	export let settings: any;
	export const icon: string = '';

	const component = 'ledstrip:0';
	const MIN_LEDS = 1;
	const MAX_LEDS = 1024;

	let loading = false;
	let protocols: string[] = [];

	let numLedsInput: string = String(settings?.[component]?.num_leds ?? '');
	let protocolInput: string = settings?.[component]?.protocol ?? '';

	$: currentNumLeds = Number(settings?.[component]?.num_leds ?? 0);
	$: currentProtocol = settings?.[component]?.protocol ?? '';

	$: numLedsError = valueOutOfRange(Number(numLedsInput), MIN_LEDS, MAX_LEDS)
		? $format('min_max_error_description', {
				values: { min: MIN_LEDS, max: MAX_LEDS },
				default: 'Value must be between {min} and {max}.',
			})
		: '';

	// Fall back to the device's current protocol so the Select is never empty
	// while ListAllProtocols is still loading or has failed.
	$: protocolOptions = (protocols.length ? protocols : [currentProtocol].filter(Boolean)).map((p) => ({
		title: p.toUpperCase(),
		value: p,
	}));

	$: hasChanges =
		(numLedsInput !== '' && Number(numLedsInput) !== currentNumLeds) ||
		(protocolInput !== '' && protocolInput !== currentProtocol);
	$: canSave = hasChanges && !numLedsError && !loading;

	onMount(async () => {
		const res: any = await gen2Request(device, 'LEDStrip.ListAllProtocols', {});
		if (res?.isok) {
			protocols = res?.data?.protocols || res?.result?.protocols || [];
		}
	});

	const save = async () => {
		if (!canSave) return;
		const patch: Record<string, any> = {};
		if (Number(numLedsInput) !== currentNumLeds) patch.num_leds = Number(numLedsInput);
		if (protocolInput && protocolInput !== currentProtocol) patch.protocol = protocolInput;
		if (!Object.keys(patch).length) return;

		loading = true;
		const res: SettingRequest = await gen2Request(device, 'LEDStrip.SetConfig', {
			id: 0,
			config: patch,
		});
		if (res.isok) {
			settings[component] = { ...(settings[component] || {}), ...patch };
		}
		onSettingsResponse(res, device);
		loading = false;
	};
</script>

<div class={className}>
	<Text styleType="h2" class="mb-s">
		{$format('subtitle_pill_peripheral_led_strip', {
			default: 'LED Strip',
		})}
	</Text>

	<Text styleType="body-small" class="mb-s mt-s">
		{$format('subtitle_pill_peripheral_led_count', {
			default: 'Number of LEDs',
		})}
	</Text>
	<Input
		aria-label={$format('input_pill_peripheral_led_count', {
			default: 'Number of LEDs',
		})}
		type="number"
		size="m"
		width="full"
		min={MIN_LEDS}
		max={MAX_LEDS}
		step={1}
		info={$format('min_max_description', {
			values: { min: MIN_LEDS, max: MAX_LEDS },
			default: 'Between {min} and {max}.',
		})}
		icon="fal fa-lightbulb"
		bind:value={numLedsInput}
		disabled={loading}
		error={numLedsError}
		on:enter={save}
		id="input-pill-led-count"
	/>

	<Text styleType="body-small" class="mb-s mt-m">
		{$format('subtitle_pill_peripheral_led_model', {
			default: 'LED Model',
		})}
	</Text>
	<Select
		aria-label={$format('select_pill_peripheral_led_model', {
			default: 'Select LED model',
		})}
		size="m"
		width="full"
		disabled={loading || !protocolOptions.length}
		icon="fal fa-microchip"
		options={protocolOptions}
		value={protocolOptions.find((p) => p.value === protocolInput) || protocolOptions[0]}
		on:change={(e) => (protocolInput = e.detail['value'])}
		id="select-pill-led-model"
	/>

	<Button class="mt-m" size="m" width="full" {loading} disabled={!canSave} on:click={save}>
		{$format('button_save', { default: 'Save' })}
	</Button>
</div>
